import { useState, useEffect, useCallback } from 'react';
import apiService from '../../../services/api';

// ── Types ──────────────────────────────────────────────────────────────────────

interface AuthorityCard {
  domain: string;
  name: string;
  is_user_domain: boolean;
  source: string;
  cohort_label?: string;
  dataforseo_rank: number;
  domain_age_formatted?: string;
  domain_age_years?: number;
  domain_age_months?: number;
  social_signals?: number;
  referring_domains: number;
  total_backlinks: number;
}

interface TacticalSuggestion {
  category: string;
  icon: string;
  title: string;
  description: string;
}

interface KeywordGap {
  id: number;
  keyword: string;
  search_volume: number;
  difficulty: number;
  intent: string;
  target_module: string;
  is_gmb_local: boolean;
  user_rank: number | null;
  competitor_ranks: Record<string, number | null>;
  best_competitor_domain: string | null;
  best_competitor_rank: number | null;
  rank_gap: number | null;
  opportunity_score: number | null;
  opportunity_tier: 'High' | 'Medium' | 'Low' | 'N/A';
  is_targeted: boolean;
  tactical_suggestions: TacticalSuggestion[];
}

interface BenchmarkData {
  user_domain: string;
  authority_cards: AuthorityCard[];
  keyword_gaps: KeywordGap[];
  last_updated: string;
  from_cache: boolean;
}

interface CompetitorBenchmarkProps {
  userId: string;
  darkMode?: boolean;
  onTooltipShow: (title: string, content: string) => void;
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function fmtNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toString();
}

function opportunityBadge(tier: string) {
  switch (tier) {
    case 'High':   return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400';
    case 'Medium': return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
    case 'Low':    return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400';
    case 'N/A':    return 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400';
    default:       return 'bg-gray-100 text-gray-600';
  }
}

function intentBadge(intent: string) {
  switch (intent?.toLowerCase()) {
    case 'transactional': return 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400';
    case 'commercial':    return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400';
    case 'navigational':  return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
    default:              return 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400';
  }
}

function rankDisplay(rank: number | null | undefined): string {
  if (rank == null) return '\u2014';
  return `#${Math.round(rank)}`;
}

function authorityTextColor(rank: number): string {
  if (rank >= 60) return 'text-emerald-600 dark:text-emerald-400';
  if (rank >= 40) return 'text-amber-600 dark:text-amber-400';
  if (rank >= 20) return 'text-orange-600 dark:text-orange-400';
  return 'text-rose-600 dark:text-rose-400';
}

function authorityBarFill(rank: number): string {
  if (rank >= 60) return 'bg-emerald-500';
  if (rank >= 40) return 'bg-amber-500';
  if (rank >= 20) return 'bg-orange-500';
  return 'bg-rose-500';
}

const CATEGORY_COLORS: Record<string, string> = {
  'Brand & Metadata':   'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
  'Page Optimization':  'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
  'Supporting Content': 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400',
  'Internal Linking':   'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
};

// ── Main Component ─────────────────────────────────────────────────────────────

export default function CompetitorBenchmark({
  userId,
  darkMode = false,
  onTooltipShow,
}: CompetitorBenchmarkProps) {
  const [data, setData] = useState<BenchmarkData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCompetitor, setSelectedCompetitor] = useState<string>('all');
  const [targetingIds, setTargetingIds] = useState<Record<number, boolean>>({});
  const [suggestionsModal, setSuggestionsModal] = useState<{
    keyword: string;
    suggestions: TacticalSuggestion[];
  } | null>(null);

  const fetchData = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const resp: any = await apiService.getCompetitorBenchmark(userId);
      setData(resp?.data || resp);
    } catch (err: any) {
      setError(err?.message || 'Failed to load competitor benchmark data.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleTarget = async (gap: KeywordGap) => {
    const newTargeted = !gap.is_targeted;
    setTargetingIds(prev => ({ ...prev, [gap.id]: true }));
    setData(prev => prev ? {
      ...prev,
      keyword_gaps: prev.keyword_gaps.map(k =>
        k.id === gap.id ? { ...k, is_targeted: newTargeted } : k
      ),
    } : prev);
    try {
      const resp: any = await apiService.targetCompetitorKeyword(userId, gap.id, newTargeted);
      const result = resp?.data || resp;
      if (result?.success) {
        setData(prev => prev ? {
          ...prev,
          keyword_gaps: prev.keyword_gaps.map(k =>
            k.id === gap.id
              ? { ...k, is_targeted: result.is_targeted, tactical_suggestions: result.tactical_suggestions || [] }
              : k
          ),
        } : prev);
        if (newTargeted && result.tactical_suggestions?.length > 0) {
          setSuggestionsModal({ keyword: gap.keyword, suggestions: result.tactical_suggestions });
        }
      }
    } catch {
      setData(prev => prev ? {
        ...prev,
        keyword_gaps: prev.keyword_gaps.map(k =>
          k.id === gap.id ? { ...k, is_targeted: gap.is_targeted } : k
        ),
      } : prev);
    } finally {
      setTargetingIds(prev => ({ ...prev, [gap.id]: false }));
    }
  };

  const competitorCards = data?.authority_cards?.filter(c => !c.is_user_domain) || [];
  const selectedCompCard = competitorCards.find(c => c.domain === selectedCompetitor);
  const maxVolume = Math.max(...(data?.keyword_gaps || []).map(k => k.search_volume || 0), 1);

  const filterOptions = [
    { value: 'all', label: 'All Competitors (Top Opportunity View)' },
    ...competitorCards.map(c => ({ value: c.domain, label: `${c.cohort_label || c.name} (${c.domain})` })),
  ];

  const filteredGaps = (data?.keyword_gaps || []).slice().sort((a, b) => {
    if (selectedCompetitor === 'all') {
      return (b.opportunity_score || 0) - (a.opportunity_score || 0);
    }
    const aRank = a.competitor_ranks?.[selectedCompetitor] ?? 999;
    const bRank = b.competitor_ranks?.[selectedCompetitor] ?? 999;
    return aRank - bRank;
  });

  // Loading skeleton
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700 animate-pulse">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-blue-200 dark:bg-blue-800 rounded-xl" />
            <div className="flex-1 space-y-2">
              <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-64" />
              <div className="h-3 bg-gray-100 dark:bg-gray-600 rounded w-48" />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow border border-gray-200 dark:border-gray-700 animate-pulse">
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-16 mb-3" />
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-10 mb-2" />
              <div className="h-1.5 bg-gray-100 dark:bg-gray-600 rounded-full" />
            </div>
          ))}
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 p-6 animate-pulse space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-12 bg-gray-100 dark:bg-gray-700 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error || !data) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl p-10 shadow-lg border border-gray-200 dark:border-gray-700 text-center">
        <div className="w-14 h-14 bg-rose-100 dark:bg-rose-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
          <i className="ri-error-warning-line text-2xl text-rose-500" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          Failed to load Competitor Benchmark
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 max-w-sm mx-auto">
          {error || 'An unexpected error occurred.'}
        </p>
        <button
          onClick={fetchData}
          className="px-5 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
        >
          <i className="ri-refresh-line mr-2" />Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0">
              <i className="ri-bar-chart-box-line text-xl text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Competitor Benchmark</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                Comparative SEO intelligence &mdash; authority scores &amp; keyword gap analysis
              </p>
              {data.last_updated && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 flex items-center gap-1.5">
                  <i className="ri-calendar-line" />
                  Last updated: {data.last_updated}
                  {data.from_cache && (
                    <span className="ml-1 px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-xs rounded-full">Cached</span>
                  )}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => onTooltipShow('Competitor Benchmark', 'Tracks authority scores and keyword performance gaps across five competitors to surface actionable SEO opportunities.')}
              className="p-2 text-gray-400 hover:text-blue-500 transition-colors rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20"
            >
              <i className="ri-information-line text-lg" />
            </button>
          </div>
        </div>
      </div>

      {/* Authority Score Panel */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <i className="ri-shield-star-line text-blue-500" />
            Domain Authority Scores
          </h3>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {(data?.authority_cards || []).map((card, idx) => (
            <div
              key={card.domain}
              className={`bg-white dark:bg-gray-800 rounded-xl p-4 shadow border transition-all ${
                card.is_user_domain
                  ? 'border-blue-300 dark:border-blue-600 ring-2 ring-blue-100 dark:ring-blue-900/30'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold ${
                  card.is_user_domain
                    ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                    : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                }`}>
                  {card.is_user_domain ? 'U' : `${idx}`}
                </div>
                {card.is_user_domain
                  ? <span className="text-xs font-semibold px-1.5 py-0.5 bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 rounded-full">You</span>
                  : card.source === 'onboarding'
                    ? <span className="text-xs text-gray-400 dark:text-gray-500">Selected</span>
                    : <span className="text-xs text-gray-300 dark:text-gray-600">SERP</span>
                }
              </div>
              <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 truncate mb-0.5" title={card.domain}>
                {card.domain}
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mb-3 truncate">
                {card.is_user_domain ? 'Your Site' : (card.cohort_label || card.name)}
              </p>
              <div className="mb-3">
                <div className="flex items-end justify-between mb-1.5">
                  <span className="text-xs text-gray-400 dark:text-gray-500">Authority</span>
                  <span className={`text-2xl font-bold leading-none ${authorityTextColor(card.dataforseo_rank)}`}>
                    {card.dataforseo_rank}
                  </span>
                </div>
                <div className="h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-700 ${authorityBarFill(card.dataforseo_rank)}`}
                    style={{ width: `${card.dataforseo_rank}%` }}
                  />
                </div>
              </div>
              <div className="space-y-1.5 pt-3 border-t border-gray-100 dark:border-gray-700">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-400 dark:text-gray-500">Domain Age</span>
                  <span className="font-medium text-gray-700 dark:text-gray-300">{card.domain_age_formatted || (card.domain_age_years ? `${card.domain_age_years}y` : 'N/A')}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-400 dark:text-gray-500">Ref. Dom.</span>
                  <span className="font-medium text-gray-700 dark:text-gray-300">{fmtNumber(card.referring_domains)}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-400 dark:text-gray-500">Backlinks</span>
                  <span className="font-medium text-gray-700 dark:text-gray-300">{fmtNumber(card.total_backlinks)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Keyword Gap Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="p-5 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <i className="ri-table-line text-blue-500" />
                Keyword Gap Analysis
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {(data?.keyword_gaps || []).length} locked keywords &middot; {(data?.keyword_gaps || []).filter(k => k.is_gmb_local).length} GMB local (N/A for SEO benchmarking)
                {selectedCompetitor !== 'all' && (
                  <span className="ml-2 font-medium text-blue-600 dark:text-blue-400">
                    &bull; Viewing vs {selectedCompCard?.cohort_label || selectedCompCard?.name || selectedCompetitor}
                  </span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-filter-3-line text-gray-400 text-sm" />
              <select
                value={selectedCompetitor}
                onChange={e => setSelectedCompetitor(e.target.value)}
                className="text-sm bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-1.5 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {filterOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                <th className="text-left px-5 py-3 font-semibold text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wide min-w-[200px]">Keyword</th>
                <th className="text-center px-3 py-3 font-semibold text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wide whitespace-nowrap">Volume</th>
                <th className="text-center px-3 py-3 font-semibold text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wide whitespace-nowrap">KD</th>
                <th className="text-center px-3 py-3 font-semibold text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wide whitespace-nowrap">Your Rank</th>
                <th className="text-center px-4 py-3 font-semibold text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wide min-w-[170px] whitespace-nowrap">
                  {selectedCompetitor === 'all'
                    ? 'Best Competitor Rank'
                    : `${selectedCompCard?.cohort_label || selectedCompCard?.name || selectedCompetitor} Rank`}
                </th>
                {selectedCompetitor !== 'all' && (
                  <th className="text-center px-4 py-3 font-semibold text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wide min-w-[140px] whitespace-nowrap">
                    Rank Difference
                  </th>
                )}
                <th className="text-center px-4 py-3 font-semibold text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wide whitespace-nowrap">Opportunity</th>
                <th className="text-center px-4 py-3 font-semibold text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wide whitespace-nowrap">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
              {filteredGaps.length === 0 ? (
                <tr>
                  <td colSpan={selectedCompetitor === 'all' ? 7 : 8} className="text-center py-14 text-gray-400 dark:text-gray-500">
                    <i className="ri-search-line text-3xl block mb-2 opacity-50" />
                    No keywords match the current filter.
                  </td>
                </tr>
              ) : (
                filteredGaps.map(gap => {
                  const isTargeting = targetingIds[gap.id];
                  const compRank = selectedCompetitor === 'all'
                    ? gap.best_competitor_rank
                    : gap.competitor_ranks?.[selectedCompetitor];

                  // Calculate opportunity specific to selected competitor
                  let rowOppTier = gap.opportunity_tier;
                  let rowOppScore = gap.opportunity_score;

                  if (selectedCompetitor !== 'all' && !gap.is_gmb_local) {
                    const isOutranked = compRank != null && (gap.user_rank == null || gap.user_rank > compRank);
                    const rankGap = isOutranked && gap.user_rank != null && compRank != null
                      ? Math.min(gap.user_rank - compRank, 100)
                      : (isOutranked && compRank != null ? Math.min(100 - compRank, 100) : 0);
                    const volScore = (gap.search_volume / maxVolume) * 100;
                    const diffScore = 100 - gap.difficulty;
                    rowOppScore = Math.round((volScore * 0.4) + (diffScore * 0.2) + (rankGap * 0.4));
                    rowOppTier = rowOppScore >= 70 ? 'High' : rowOppScore >= 40 ? 'Medium' : 'Low';
                  }

                  return (
                    <tr
                      key={gap.id}
                      className={`transition-colors ${
                        gap.is_targeted
                          ? 'bg-blue-50/50 dark:bg-blue-900/10'
                          : 'hover:bg-gray-50/80 dark:hover:bg-gray-750/50'
                      }`}
                    >
                      <td className="px-5 py-3.5 min-w-[200px]">
                        <p className="font-medium text-gray-900 dark:text-white text-sm leading-snug">{gap.keyword}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs text-gray-400 dark:text-gray-500 uppercase font-medium">
                            {gap.target_module === 'gmb' ? 'GMB' : 'SEO'}
                          </span>
                          {gap.is_targeted && (
                            <span className="flex items-center gap-0.5 text-xs font-semibold text-blue-600 dark:text-blue-400">
                              <i className="ri-focus-3-line" />Targeted
                            </span>
                          )}
                        </div>
                      </td>

                      <td className="px-3 py-3.5 text-center whitespace-nowrap">
                        <span className="font-medium text-gray-700 dark:text-gray-300">{fmtNumber(gap.search_volume)}</span>
                      </td>

                      <td className="px-3 py-3.5 text-center whitespace-nowrap">
                        <span className={`font-bold text-sm ${
                          gap.difficulty >= 70 ? 'text-rose-600 dark:text-rose-400' :
                          gap.difficulty >= 40 ? 'text-amber-600 dark:text-amber-400' :
                                                 'text-emerald-600 dark:text-emerald-400'
                        }`}>{gap.difficulty}</span>
                      </td>

                      <td className="px-3 py-3.5 text-center whitespace-nowrap">
                        {gap.is_gmb_local ? (
                          <span className="text-xs text-gray-300 dark:text-gray-600">&mdash;</span>
                        ) : (
                          <span className={`font-semibold ${
                            gap.user_rank != null && gap.user_rank <= 10 ? 'text-emerald-600 dark:text-emerald-400' :
                            gap.user_rank != null && gap.user_rank <= 30 ? 'text-amber-600 dark:text-amber-400' :
                            'text-gray-500 dark:text-gray-400'
                          }`}>
                            {rankDisplay(gap.user_rank)}
                          </span>
                        )}
                      </td>

                      <td className="px-4 py-3.5 text-center min-w-[170px]">
                        {gap.is_gmb_local ? (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${opportunityBadge('N/A')}`}>N/A</span>
                        ) : selectedCompetitor === 'all' ? (
                          gap.best_competitor_rank != null ? (
                            <div>
                              <p className="font-semibold text-sm text-gray-700 dark:text-gray-300">{rankDisplay(gap.best_competitor_rank)}</p>
                              {gap.best_competitor_domain && (
                                <p className="text-xs text-gray-400 dark:text-gray-500 truncate max-w-[150px] mx-auto" title={gap.best_competitor_domain}>
                                  {gap.best_competitor_domain}
                                </p>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400 dark:text-gray-500">Not in top 100</span>
                          )
                        ) : (
                          compRank != null ? (
                            <p className="font-semibold text-sm text-gray-800 dark:text-gray-200">
                              {rankDisplay(compRank)}
                            </p>
                          ) : (
                            <span className="text-xs text-gray-400 dark:text-gray-500">Not Ranking (&gt;100)</span>
                          )
                        )}
                      </td>

                      {selectedCompetitor !== 'all' && (
                        <td className="px-4 py-3.5 text-center min-w-[140px] whitespace-nowrap">
                          {gap.is_gmb_local ? (
                            <span className="text-xs text-gray-300 dark:text-gray-600">&mdash;</span>
                          ) : gap.user_rank != null && compRank != null ? (
                            gap.user_rank > compRank ? (
                              <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-0.5 bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400 rounded-full">
                                Outranks (+{Math.round(gap.user_rank - compRank)})
                              </span>
                            ) : gap.user_rank < compRank ? (
                              <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-0.5 bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 rounded-full">
                                Ahead (+{Math.round(compRank - gap.user_rank)})
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 rounded-full text-xs font-semibold">
                                Tied
                              </span>
                            )
                          ) : gap.user_rank == null && compRank != null ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 bg-rose-50 text-rose-600 dark:bg-rose-900/20 dark:text-rose-400 rounded-full text-xs font-medium">
                              Behind (&gt;100)
                            </span>
                          ) : gap.user_rank != null && compRank == null ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400 rounded-full text-xs font-medium">
                              Ahead (&gt;100)
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400 dark:text-gray-500">&mdash;</span>
                          )}
                        </td>
                      )}

                      <td className="px-4 py-3.5 text-center whitespace-nowrap">
                        {gap.is_gmb_local ? (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${opportunityBadge('N/A')}`}>
                            N/A &mdash; Local GMB
                          </span>
                        ) : (
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${opportunityBadge(rowOppTier)}`}>
                            {rowOppTier}
                            {rowOppScore != null && (
                              <span className="ml-1 opacity-60">({rowOppScore})</span>
                            )}
                          </span>
                        )}
                      </td>

                      <td className="px-4 py-3.5 text-center">
                        <div className="flex items-center justify-center gap-1.5">
                          <button
                            onClick={() => handleTarget(gap)}
                            disabled={isTargeting}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                              isTargeting
                                ? 'opacity-60 cursor-not-allowed bg-gray-100 dark:bg-gray-700 text-gray-400'
                                : gap.is_targeted
                                  ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
                                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:text-blue-600 dark:hover:text-blue-400 border border-gray-200 dark:border-gray-600'
                            }`}
                          >
                            <i className={`text-xs ${
                              isTargeting ? 'ri-loader-4-line animate-spin' :
                              gap.is_targeted ? 'ri-focus-3-line' : 'ri-crosshair-line'
                            }`} />
                            {gap.is_targeted ? 'Targeted \u2713' : 'Target'}
                          </button>
                          {gap.is_targeted && (gap.tactical_suggestions || []).length > 0 && (
                            <button
                              onClick={() => setSuggestionsModal({ keyword: gap.keyword, suggestions: gap.tactical_suggestions })}
                              className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors border border-purple-200 dark:border-purple-800"
                            >
                              <i className="ri-lightbulb-line text-xs" />Tips
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Legend */}
        <div className="px-5 py-3 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex items-center flex-wrap gap-4 text-xs text-gray-500 dark:text-gray-400">
          {(['High', 'Medium', 'Low', 'N/A'] as const).map(tier => (
            <div key={tier} className="flex items-center gap-1.5">
              <span className={`inline-flex px-1.5 py-0.5 rounded-full text-xs font-semibold ${opportunityBadge(tier)}`}>{tier}</span>
              {tier === 'High' && <span>Score 70-100</span>}
              {tier === 'Medium' && <span>Score 40-69</span>}
              {tier === 'Low' && <span>Score 0-39</span>}
              {tier === 'N/A' && <span>Local GMB &mdash; not applicable for organic SEO</span>}
            </div>
          ))}
        </div>
      </div>

      {/* Disclaimer */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-100 dark:border-blue-800">
        <div className="flex items-start space-x-3">
          <i className="ri-information-line text-blue-500 text-lg flex-shrink-0 mt-0.5" />
          <p className="text-xs text-blue-700 dark:text-blue-300 leading-relaxed">
            <span className="font-semibold">Competitive Intelligence Disclaimer: </span>
            All metrics are estimates based on publicly available search engine data and should be used for strategic guidance, not absolute measurement. Competitor rank positions reflect the last available SERP data and may vary.
          </p>
        </div>
      </div>

      {/* Tactical Suggestions Modal */}
      {suggestionsModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)' }}
          onClick={e => { if (e.target === e.currentTarget) setSuggestionsModal(null); }}
        >
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 dark:border-gray-700">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
                    <i className="ri-lightbulb-line text-white text-lg" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">Tactical Action Plan</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Close the competitive gap for:</p>
                    <p className="text-sm font-semibold text-blue-600 dark:text-blue-400 mt-0.5">
                      "{suggestionsModal.keyword}"
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setSuggestionsModal(null)}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex-shrink-0"
                >
                  <i className="ri-close-line text-xl" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              {suggestionsModal.suggestions.map((s, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-4 p-4 rounded-xl border border-gray-100 dark:border-gray-700 hover:border-gray-200 dark:hover:border-gray-600 transition-colors"
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${CATEGORY_COLORS[s.category] || 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}>
                    <i className={`${s.icon} text-lg`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className={`inline-flex items-center text-xs font-semibold px-1.5 py-0.5 rounded-full mb-1.5 ${CATEGORY_COLORS[s.category] || 'bg-gray-100 text-gray-500'}`}>
                      {s.category}
                    </span>
                    <p className="font-semibold text-sm text-gray-900 dark:text-white mb-1">{s.title}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{s.description}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="p-6 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-b-2xl flex items-center justify-between flex-wrap gap-3">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                <i className="ri-focus-3-line text-blue-500 mr-1" />
                This keyword is prioritized in downstream content generation.
              </p>
              <button
                onClick={() => setSuggestionsModal(null)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

